import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getBeautyAvailabilityApi,
  holdBeautyAppointmentApi,
  createBeautyReservationApi,
  getAppSettings,
} from "../../api/api";
import { FISERV_CARD_OPTIONS } from "../../constants/fiservPaymentOptions";
import { COUNTRIES, resolveUrl } from "../reservas/components/ReservaShared";
import {
  addMinutesToTime,
  bookingClientFields,
  cartTotals,
  clientFieldAnswersPayload,
  clientFieldErrorMessage,
  clientFieldErrors,
  isDateBookable,
  lineasDeCarrito,
  missingServicesFor,
  payAtVenue,
} from "./beautyBooking";
import { itemsDeSeleccion, mensajeDeSeleccion, seleccionCompleta } from "../../helpers/beautyLineas";
import { waitlistPrefill, waitlistTokenFromSearch } from "../listaDeEspera/waitlistModel";
import { isOnlineBookingDisabled } from "../reservas/onlineBooking";

// Beauty-namespaced sessionStorage keys; they never collide with the bar or
// court flows.
export const BEAUTY_HOLD_STATE_KEY = "beauty_reservation:hold_state";
export const BEAUTY_LAST_KEY = "beauty_reservation:last";
// Selection saved before the login redirect, restored (and held) after it.
export const BEAUTY_PENDING_SELECTION_KEY = "beauty_reservation:pending_selection";

const readSavedHold = (salonId, services) => {
  try {
    const raw = sessionStorage.getItem(BEAUTY_HOLD_STATE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved?.hold?.holdId || !saved.hold.expiresAt || new Date(saved.hold.expiresAt) <= new Date()) {
      sessionStorage.removeItem(BEAUTY_HOLD_STATE_KEY);
      return null;
    }
    if (String(saved.salonId) !== String(salonId)) return null;
    const known = new Set((services || []).map((s) => String(s._id)));
    if (!Array.isArray(saved.cartIds) || !saved.cartIds.length || !saved.cartIds.every((id) => known.has(String(id)))) {
      return null;
    }
    return saved;
  } catch (_) {
    return null;
  }
};

const removeSavedHold = () => {
  try {
    sessionStorage.removeItem(BEAUTY_HOLD_STATE_KEY);
  } catch (_) {}
};

const readPendingSelection = (salonId, services) => {
  try {
    const raw = sessionStorage.getItem(BEAUTY_PENDING_SELECTION_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw);
    if (String(pending?.salonId) !== String(salonId)) return null;
    const known = new Set((services || []).map((s) => String(s._id)));
    const valid =
      Array.isArray(pending.cartIds) &&
      pending.cartIds.length > 0 &&
      pending.cartIds.every((id) => known.has(String(id))) &&
      pending.date &&
      pending.slot?.slotStart;
    return valid ? pending : null;
  } catch (_) {
    return null;
  }
};

const removePendingSelection = () => {
  try {
    sessionStorage.removeItem(BEAUTY_PENDING_SELECTION_KEY);
  } catch (_) {}
};

/** Hold state from the back response. The hold does not echo date/time: the slot start is kept here. */
const holdFromResponse = (info, slotStart) => {
  const holdServices = Array.isArray(info?.services) ? info.services : [];
  return {
    holdId: info?.holdId,
    expiresAt: info?.expiresAt,
    professional: info?.professional || null,
    services: holdServices,
    totalPrice: info?.totalPrice ?? null,
    depositAmount: Number(info?.depositAmount) || 0,
    slotStart,
    slotEnd: addMinutesToTime(slotStart, cartTotals(holdServices).durationMinutes),
  };
};

/** Candidate durations of an "any" slot, from the availability professionals. */
const candidateDurations = (data, slot) => {
  const ids = (slot?.professionalIds || []).map(String);
  return (data?.professionals || [])
    .filter((p) => ids.includes(String(p.id)))
    .map((p) => Number(p.totalDurationMinutes))
    .filter((n) => Number.isFinite(n) && n > 0);
};

/**
 * "hasta" time of a slot. Specific mode: the back sends slotEnd. Any mode:
 * only when every candidate professional shares the same total duration,
 * otherwise null (the UI omits the sub-line).
 */
export const slotEndTime = (data, slot) => {
  if (!slot) return null;
  if (data?.mode !== "any") return slot.slotEnd || null;
  const durations = candidateDurations(data, slot);
  if (!durations.length || durations.some((d) => d !== durations[0])) return null;
  return addMinutesToTime(slot.slotStart, durations[0]);
};

/** True when `minutes` goes over the professional's own limit (null/absent limit = no limit). */
export const exceedsMaxDuration = (professional, minutes) => {
  const max = Number(professional?.maxAppointmentMinutes);
  return Number.isFinite(max) && max > 0 && Number(minutes) > max;
};

const isTakenMessage = (message) => /ya no está disponible|no hay profesionales/i.test(message || "");
const isDepositMessage = (message) => /seña|deposit/i.test(message || "");

const searchedDate = (state, salon) => {
  const value = state?.date;
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && isDateBookable(value, salon) ? value : null;
};

/**
 * Beauty booking flow (buyer side): cart, professional, date, availability,
 * slot, 5-minute hold, contact form and reservation creation. Auth is optional
 * in the back: the token is sent when present, guests can book.
 */
/**
 * `options.linkedProfessionalId` turns on the professional's personal-link mode:
 * that professional starts selected and is never reset by cart changes, a
 * restored selection for anyone else is ignored and the reservation is flagged
 * `viaProfessionalLink`. `options.onLeaveLink` is the "Cambiar profesional"
 * action (the caller navigates to the normal salon flow).
 */
export function useBeautyBookingFlow({ salon, services = [], professionals = [] }, options = {}) {
  const { linkedProfessionalId = null, onLeaveLink } = options;
  const salonId = salon?._id || null;
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state) => state.auth?.accessToken);
  const authUser = useSelector((state) => state.auth?.user || {});

  // A hold still alive in sessionStorage resumes the booking (reload on
  // Resumen / Datos).
  const savedRef = useRef(undefined);
  // In link mode a restored selection that belongs to another professional is ignored.
  const matchesLink = (selection) =>
    !linkedProfessionalId || String(selection?.professionalId ?? "") === String(linkedProfessionalId);
  if (savedRef.current === undefined) {
    const found = readSavedHold(salonId, services);
    savedRef.current = found && matchesLink(found) ? found : null;
  }
  const saved = savedRef.current;

  // A selection saved before a login redirect is restored once (and held again
  // when the buyer came back logged in). A live hold wins over it.
  const pendingRef = useRef(undefined);
  if (pendingRef.current === undefined) {
    const found = saved ? null : readPendingSelection(salonId, services);
    pendingRef.current = found && matchesLink(found) ? found : null;
  }
  const pending = pendingRef.current;
  const pendingStartedRef = useRef(false);
  const initial = saved || pending;

  // Viene del aviso de la lista de espera: abre con los servicios y el profesional anotados (el día lo toma
  // la misma ruta que la búsqueda del listado) y el token viaja con la reserva.
  const waitlistToken = waitlistTokenFromSearch(location.search);
  const wlInit = useMemo(() => {
    if (!waitlistToken || initial) return null;
    const criteria = waitlistPrefill(location.state)?.criteria || {};
    const known = new Set((services || []).map((s) => String(s._id)));
    const items = (Array.isArray(criteria.items) ? criteria.items : []).filter((i) => i && known.has(String(i.serviceId)));
    if (!items.length) return null;
    const choices = {};
    items.forEach((i) => {
      if (i.variantId || (i.addOnIds || []).length) {
        choices[String(i.serviceId)] = { variantId: i.variantId || "", addOnIds: [...(i.addOnIds || [])].map(String) };
      }
    });
    return { cartIds: items.map((i) => String(i.serviceId)), choices, professionalId: criteria.professionalId || null };
    // Se calcula una sola vez: solo siembra el estado inicial.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Selection ----
  const [cartIds, setCartIds] = useState(() => (initial ? initial.cartIds.map(String) : wlInit?.cartIds || []));
  // { [serviceId]: { variantId, addOnIds } }: what the buyer chose in the services that have options.
  const [cartChoices, setCartChoices] = useState(() => initial?.choices || wlInit?.choices || {});
  const [professionalId, setProfessionalId] = useState(() => linkedProfessionalId || initial?.professionalId || wlInit?.professionalId || null);
  // A date picked on the listing's availability search (`location.state.date`)
  // opens the booking with that day selected, when the salon can take it.
  const [date, setDateState] = useState(() => initial?.date || searchedDate(location.state, salon) || null);
  const [selectedSlot, setSelectedSlot] = useState(() => initial?.slot || null);

  // Latest cart, so several add/remove calls in one tick do not overwrite each other.
  const cartRef = useRef(cartIds);
  cartRef.current = cartIds;
  const choicesRef = useRef(cartChoices);
  choicesRef.current = cartChoices;

  const cartServices = useMemo(
    () => cartIds.map((id) => services.find((s) => String(s._id) === id)).filter(Boolean),
    [cartIds, services],
  );
  // The selection the back receives: one item per service, with its variant and add-ons.
  const selection = useMemo(
    () => cartIds.map((id) => ({ serviceId: id, variantId: cartChoices[id]?.variantId || "", addOnIds: cartChoices[id]?.addOnIds || [] })),
    [cartIds, cartChoices],
  );
  const items = useMemo(() => itemsDeSeleccion(selection), [selection]);
  const selectionComplete = useMemo(() => seleccionCompleta(selection, services), [selection, services]);
  const cartLines = useMemo(() => lineasDeCarrito(cartServices, cartChoices), [cartServices, cartChoices]);
  const cartKey = JSON.stringify(items);

  // ---- Availability ----
  const [availability, setAvailability] = useState({ loading: false, error: "", data: null });
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    // The API answers 403 for a salon with online booking off: don't even ask.
    if (!salonId || !date || cartIds.length === 0 || !selectionComplete || isOnlineBookingDisabled(salon)) {
      setAvailability({ loading: false, error: "", data: null });
      return undefined;
    }
    const requestId = ++requestRef.current;
    setAvailability((prev) => ({ ...prev, loading: true, error: "" }));
    getBeautyAvailabilityApi({ salonId, date, items, professionalId: professionalId || undefined })
      .then((response) => {
        if (requestId !== requestRef.current) return;
        setAvailability({ loading: false, error: "", data: response?.data?.info || null });
      })
      .catch((err) => {
        if (requestId !== requestRef.current) return;
        setAvailability({
          loading: false,
          error: mensajeDeSeleccion(err) || err?.response?.data?.message || "No pudimos cargar los horarios. Intentá de nuevo.",
          data: null,
        });
      });
    return () => {
      requestRef.current += 1;
    };
    // cartKey stands for the whole selection (ids, variants and add-ons).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonId, date, cartKey, professionalId, reloadKey]);

  const reloadAvailability = useCallback(() => setReloadKey((k) => k + 1), []);

  // ---- Hold ----
  const [hold, setHold] = useState(() => (saved ? { ...saved.hold, slotStart: saved.slot?.slotStart, slotEnd: saved.hold.slotEnd || null } : null));
  const [holdLoading, setHoldLoading] = useState(false);
  const [holdError, setHoldError] = useState("");
  const [holdExpired, setHoldExpired] = useState(false);
  const finishedRef = useRef(false);
  // Identifies the latest hold request: a response (or 409) that no longer
  // matches the current selection is ignored, like `requestRef` for availability.
  const holdRequestRef = useRef(0);
  // Where the booking opens on mount: "resumen" (live hold, mobile Resumen /
  // desktop Tus datos), "pending" (re-creating the hold after login) or
  // "slots" (selection restored without a hold), else null.
  const [restoredStep, setRestoredStep] = useState(() => {
    if (saved) return "resumen";
    if (pending) return token ? "pending" : "slots";
    return null;
  });

  // Persist the active hold so a reload resumes it while it is still valid.
  useEffect(() => {
    if (!hold || !selectedSlot || !date || finishedRef.current) return;
    try {
      sessionStorage.setItem(
        BEAUTY_HOLD_STATE_KEY,
        JSON.stringify({ salonId, cartIds, choices: cartChoices, professionalId, date, slot: selectedSlot, hold }),
      );
    } catch (_) {}
  }, [hold, selectedSlot, date, salonId, cartIds, cartChoices, professionalId]);

  const clearHold = useCallback(() => {
    setHold(null);
    removeSavedHold();
  }, []);

  // Any change to the selection invalidates the slot and its hold (the back
  // has no release endpoint, the hold simply lapses).
  const resetSelection = useCallback(() => {
    holdRequestRef.current += 1;
    setHoldLoading(false);
    setSelectedSlot(null);
    setHoldError("");
    setHoldExpired(false);
    clearHold();
  }, [clearHold]);

  // ---- Cart / professional / date ----
  const eligibleAfter = useCallback(
    (nextIds, nextChoices, currentProfessionalId) => {
      if (linkedProfessionalId) return currentProfessionalId;
      if (!currentProfessionalId) return null;
      const professional = professionals.find((p) => String(p.id) === String(currentProfessionalId));
      const nextServices = nextIds.map((id) => services.find((s) => String(s._id) === id)).filter(Boolean);
      if (!professional || missingServicesFor(professional, nextServices).length > 0) return null;
      const minutes = cartTotals(lineasDeCarrito(nextServices, nextChoices)).durationMinutes;
      return exceedsMaxDuration(professional, minutes) ? null : currentProfessionalId;
    },
    [professionals, services, linkedProfessionalId],
  );

  const changeCart = useCallback(
    (nextIds, nextChoices = choicesRef.current) => {
      cartRef.current = nextIds;
      choicesRef.current = nextChoices;
      setCartIds(nextIds);
      setCartChoices(nextChoices);
      setProfessionalId((current) => eligibleAfter(nextIds, nextChoices, current));
      resetSelection();
    },
    [eligibleAfter, resetSelection],
  );

  const copyChoice = (choice) => (choice ? { variantId: choice.variantId || "", addOnIds: [...(choice.addOnIds || [])] } : null);

  // `choice` = { variantId, addOnIds } for a service that has options; plain services have none.
  const addService = useCallback(
    (service, choice) => {
      const id = String(service?._id ?? service);
      if (cartRef.current.includes(id)) return;
      const picked = copyChoice(choice);
      changeCart([...cartRef.current, id], picked ? { ...choicesRef.current, [id]: picked } : choicesRef.current);
    },
    [changeCart],
  );
  // Edit what was chosen in a service that is already in the cart.
  const updateService = useCallback(
    (service, choice) => {
      const id = String(service?._id ?? service);
      const picked = copyChoice(choice);
      if (!cartRef.current.includes(id) || !picked) return;
      changeCart(cartRef.current, { ...choicesRef.current, [id]: picked });
    },
    [changeCart],
  );
  const removeService = useCallback(
    (service) => {
      const id = String(service?._id ?? service);
      if (!cartRef.current.includes(id)) return;
      const { [id]: _removed, ...rest } = choicesRef.current;
      changeCart(cartRef.current.filter((x) => x !== id), rest);
    },
    [changeCart],
  );

  const selectProfessional = useCallback(
    (id) => {
      setProfessionalId(id || null);
      resetSelection();
    },
    [resetSelection],
  );

  const setDate = useCallback(
    (value) => {
      setDateState(value || null);
      resetSelection();
    },
    [resetSelection],
  );

  const dateBookable = useCallback((value) => isDateBookable(value, salon), [salon]);

  // Effective total of a professional for the cart: availability when it has
  // one for them (overrides applied), else the base cart total.
  const professionalOptions = useMemo(() => {
    const data = availability.data;
    const base = cartTotals(cartLines).durationMinutes;
    const totalFor = (professional) => {
      const entry = (data?.professionals || []).find((p) => String(p.id) === String(professional.id));
      if (entry && Number(entry.totalDurationMinutes) > 0) return Number(entry.totalDurationMinutes);
      if (data?.mode === "specific" && String(professionalId) === String(professional.id) && Number(data.totalDurationMinutes) > 0) {
        return Number(data.totalDurationMinutes);
      }
      return base;
    };
    return professionals.map((professional) => {
      const missing = missingServicesFor(professional, cartServices);
      const entry = (data?.professionals || []).find((p) => String(p.id) === String(professional.id));
      const exceeds =
        missing.length === 0 && (entry?.exceedsMaxDuration === true || exceedsMaxDuration(professional, totalFor(professional)));
      return {
        professional,
        missing,
        exceeds,
        maxMinutes: professional.maxAppointmentMinutes ?? entry?.maxAppointmentMinutes ?? null,
        eligible: missing.length === 0 && !exceeds,
      };
    });
  }, [professionals, cartServices, cartLines, availability.data, professionalId]);

  const selectedProfessional = useMemo(
    () => professionals.find((p) => String(p.id) === String(professionalId)) || null,
    [professionals, professionalId],
  );

  // Notice when the chosen professional cannot take this cart (back flag or local check).
  const maxDurationNotice = useMemo(() => {
    const option = professionalOptions.find((o) => String(o.professional.id) === String(professionalId));
    if (!professionalId || !(availability.data?.exceedsMaxDuration || option?.exceeds)) return null;
    const name = selectedProfessional?.name || "el profesional";
    return `Con estos servicios el turno supera la duración máxima de ${name}. Quitá algún servicio o elegí otro profesional.`;
  }, [professionalOptions, professionalId, availability.data, selectedProfessional]);

  // ---- Totals ----
  const baseTotals = useMemo(() => cartTotals(cartLines), [cartLines]);

  // Effective totals: from availability when loaded, else the base prices.
  const effectiveTotals = useMemo(() => {
    const data = availability.data;
    if (data?.mode === "specific") {
      return {
        source: "availability",
        from: false,
        price: Number(data.totalPrice) || 0,
        deposit: Number(data.totalDeposit) || 0,
        durationMinutes: Number(data.totalDurationMinutes) || baseTotals.durationMinutes,
      };
    }
    if (data?.mode === "any" && Array.isArray(data.professionals) && data.professionals.length > 0) {
      // Over-limit professionals stay in the list but cannot take the slots.
      const within = data.professionals.filter((p) => !p.exceedsMaxDuration);
      const list = within.length > 0 ? within : data.professionals;
      return {
        source: "availability",
        from: true,
        price: Math.min(...list.map((p) => Number(p.totalPrice) || 0)),
        deposit: Math.min(...list.map((p) => Number(p.totalDeposit) || 0)),
        durationMinutes: Math.min(...list.map((p) => Number(p.totalDurationMinutes) || 0)),
      };
    }
    return { source: "base", from: !professionalId, ...baseTotals };
  }, [availability.data, baseTotals, professionalId]);

  const holdTotals = useMemo(() => {
    if (!hold) return null;
    const durationMinutes = cartTotals(hold.services).durationMinutes;
    const total = hold.totalPrice ?? cartTotals(hold.services).price;
    const deposit = Number(hold.depositAmount) || 0;
    return {
      durationMinutes,
      total: Number(total) || 0,
      deposit,
      payAtVenue: payAtVenue(total, deposit),
    };
  }, [hold]);

  const slotEndFor = useCallback((slot) => slotEndTime(availability.data, slot), [availability.data]);

  const selectSlot = useCallback((slot) => {
    holdRequestRef.current += 1;
    setHoldLoading(false);
    setSelectedSlot(slot || null);
    setHoldError("");
  }, []);

  const clearHoldError = useCallback(() => setHoldError(""), []);

  // ---- Expiry / conflicts ----
  const handleHoldExpired = useCallback(() => {
    clearHold();
    setSelectedSlot(null);
    setHoldExpired(true);
    setReloadKey((k) => k + 1);
  }, [clearHold]);

  const dismissExpired = useCallback(() => setHoldExpired(false), []);

  const resetAfterConflict = useCallback(
    (message) => {
      clearHold();
      setSelectedSlot(null);
      setHoldError(message);
      setReloadKey((k) => k + 1);
    },
    [clearHold],
  );

  // Remembers the selection and sends the buyer to log in; the booking comes
  // back to this page and `restoredStep` resumes it.
  const redirectToLogin = useCallback(
    (slot) => {
      try {
        sessionStorage.setItem(
          BEAUTY_PENDING_SELECTION_KEY,
          JSON.stringify({ salonId, cartIds, choices: cartChoices, professionalId, date, slotStart: slot.slotStart, slot }),
        );
      } catch (_) {}
      navigate("/login", { state: { from: location.pathname + location.search } });
    },
    [salonId, cartIds, cartChoices, professionalId, date, navigate, location],
  );

  // Maps a failed hold request to its outcome and state (shared by createHold
  // and the post-login restore).
  const handleHoldFailure = useCallback(
    (err) => {
      const message = mensajeDeSeleccion(err) || err?.response?.data?.message || "No pudimos guardar el turno. Intentá de nuevo.";
      if (err?.response?.status === 409) {
        // Deposit-unavailable keeps the selection; a taken slot does not.
        if (isDepositMessage(message) && !isTakenMessage(message)) setHoldError(message);
        else resetAfterConflict(message);
        return "conflict";
      }
      setHoldError(message);
      return "error";
    },
    [resetAfterConflict],
  );

  const createHold = useCallback(async () => {
    if (!salonId || !selectedSlot || !date || cartIds.length === 0) return null;
    // Login is required before Tus datos (same gate as Canchas).
    if (!token) {
      redirectToLogin(selectedSlot);
      return "unauthenticated";
    }
    // A live hold for this exact selection is reused (any selection change clears it).
    if (hold?.expiresAt && new Date(hold.expiresAt) > new Date() && hold.slotStart === selectedSlot.slotStart) {
      return "created";
    }
    const requestId = ++holdRequestRef.current;
    const isCurrent = () => requestId === holdRequestRef.current;
    try {
      setHoldLoading(true);
      setHoldError("");
      setHoldExpired(false);
      finishedRef.current = false;
      const response = await holdBeautyAppointmentApi(
        {
          salonId,
          items,
          professionalId: professionalId || null,
          date,
          slotStart: selectedSlot.slotStart,
        },
        token,
      );
      if (!isCurrent()) return "stale";
      setHold(holdFromResponse(response?.data?.info, selectedSlot.slotStart));
      return "created";
    } catch (err) {
      if (!isCurrent()) return "stale";
      if (err?.response?.status === 401) {
        redirectToLogin(selectedSlot);
        return "unauthenticated";
      }
      return handleHoldFailure(err);
    } finally {
      if (isCurrent()) setHoldLoading(false);
    }
  }, [salonId, selectedSlot, date, cartIds, items, professionalId, token, hold, redirectToLogin, handleHoldFailure]);

  // After the login redirect: re-create the hold for the saved selection and
  // open Tus datos / Resumen; on failure open Elegir turno with the error.
  useEffect(() => {
    if (!pending || pendingStartedRef.current) return;
    pendingStartedRef.current = true;
    removePendingSelection();
    if (!token) return; // restoredStep is already "slots"
    (async () => {
      try {
        setHoldLoading(true);
        setHoldError("");
        finishedRef.current = false;
        const response = await holdBeautyAppointmentApi(
          {
            salonId,
            items: itemsDeSeleccion(
              pending.cartIds.map((id) => ({ serviceId: String(id), ...(pending.choices?.[String(id)] || {}) })),
            ),
            professionalId: pending.professionalId || null,
            date: pending.date,
            slotStart: pending.slot.slotStart,
          },
          token,
        );
        setHold(holdFromResponse(response?.data?.info, pending.slot.slotStart));
        setRestoredStep("resumen");
      } catch (err) {
        handleHoldFailure(err);
        setRestoredStep("slots");
      } finally {
        setHoldLoading(false);
      }
    })();
    // Runs once on mount with the selection read at that time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Contact form + checkout ----
  const [consent, setConsent] = useState(false);
  // Opt-in to news and promotions: unchecked by default, never required to book.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [countryCode, setCountryCode] = useState(COUNTRIES[0].code);
  const countryRef = useRef(COUNTRIES[0]);
  countryRef.current = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      fullName: authUser?.name || "",
      email: authUser?.email || "",
      phone: "",
      dni: authUser?.dni || "",
      notes: "",
    },
  });

  useEffect(() => {
    register("phone", {
      required: "Ingresá tu teléfono.",
      validate: (value) => countryRef.current.validate(value || "") || "Este número no es válido",
    });
  }, [register]);

  useEffect(() => {
    if (authUser?.name) setValue("fullName", authUser.name);
    if (authUser?.email) setValue("email", authUser.email);
    if (authUser?.dni) setValue("dni", authUser.dni);
  }, [authUser?.name, authUser?.email, authUser?.dni, setValue]);

  const setPhone = useCallback(
    ({ countryCode: nextCountry, number }) => {
      setCountryCode(nextCountry);
      countryRef.current = COUNTRIES.find((c) => c.code === nextCountry) || COUNTRIES[0];
      setValue("phone", number, { shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  // Custom fields the salon asks at booking; answers live outside react-hook-form (one value per field id).
  const clientFields = useMemo(() => bookingClientFields(salon), [salon]);
  const [fieldAnswers, setFieldAnswers] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const setFieldAnswer = useCallback((id, value) => {
    setFieldAnswers((prev) => ({ ...prev, [id]: value }));
    setFieldErrors((prev) => {
      if (!prev[id]) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Fiserv payment modal (same state as Mesas / Canchas).
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [modalCard, setModalCard] = useState("");
  const [cardOptions, setCardOptions] = useState(FISERV_CARD_OPTIONS);
  const [pendingValues, setPendingValues] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await getAppSettings();
        const info = response?.data?.info;
        if (!active || response?.status !== 200 || !info) return;
        const options = info.fiserv_card_options || info.fiservCardOptions;
        if (Array.isArray(options) && options.length) setCardOptions(options);
      } catch (_) {}
    })();
    return () => {
      active = false;
    };
  }, []);

  const selectedCardOption = useMemo(
    () => cardOptions.find((opt) => opt.id === modalCard) || null,
    [cardOptions, modalCard],
  );

  useEffect(() => {
    if (typeof document === "undefined" || !document.body) return undefined;
    document.body.classList.toggle("payment-modal-open", showPaymentModal);
    return () => document.body.classList.remove("payment-modal-open");
  }, [showPaymentModal]);

  const closePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    setPendingValues(null);
  }, []);

  const proceedWithReservation = useCallback(
    async (values, storeId = null) => {
      if (!hold) return null;
      try {
        setSubmitting(true);
        setSubmitError("");
        let phone = (values.phone || "").trim();
        if (phone) {
          const prefix = countryRef.current?.prefix || "+54";
          const prefixDigits = prefix.replace(/\D/g, "");
          phone = `${prefix} ${phone.replace(new RegExp(`^(\\+?${prefixDigits}\\s*)`), "")}`;
        }
        const payload = {
          holdId: hold.holdId,
          name: values.fullName,
          email: values.email,
          phone,
          dni: values.dni,
          notes: values.notes,
          checkoutOrigin: "web",
        };
        const clientFieldAnswers = clientFieldAnswersPayload(clientFields, fieldAnswers);
        if (clientFieldAnswers) payload.clientFieldAnswers = clientFieldAnswers;
        if (waitlistToken) payload.waitlistToken = waitlistToken;
        if (marketingOptIn) payload.marketingOptIn = true;
        if (storeId) payload.storeId = storeId;
        // Informational flag: only when the appointment really is with the linked professional.
        if (
          linkedProfessionalId &&
          String(professionalId) === String(linkedProfessionalId) &&
          (!hold.professional?.id || String(hold.professional.id) === String(linkedProfessionalId))
        ) {
          payload.viaProfessionalLink = true;
        }
        const response = await createBeautyReservationApi(payload, token);
        const info = response?.data?.info || {};
        const reservationCode = info.reservationCode;
        const reservationSalonId = info.salonId || salonId;
        finishedRef.current = true;
        removeSavedHold();
        try {
          sessionStorage.setItem(
            BEAUTY_LAST_KEY,
            JSON.stringify({ reservationCode, salonId: reservationSalonId, qrToken: info.qrToken || "" }),
          );
        } catch (_) {}
        if (info.paymentHref) {
          window.location.href = resolveUrl(info.paymentHref);
          return "redirect";
        }
        const params = new URLSearchParams({
          type: "reservation",
          reservationCode: String(reservationCode || ""),
          code: String(reservationCode || ""),
          barId: String(reservationSalonId || ""),
          status: "approved",
          flow: "beauty",
        });
        navigate(`/reservas/success?${params.toString()}`);
        return "success";
      } catch (err) {
        const status = err?.response?.status;
        const message = err?.response?.data?.message || "";
        if (status === 409 && !(isDepositMessage(message) && !isTakenMessage(message))) {
          // Hold expired / slot taken: back to the slot step.
          resetAfterConflict(message || "El bloqueo expiró o el turno se ocupó. Elegí un horario nuevamente.");
          return "slots";
        }
        const fieldMessage = status === 400 ? clientFieldErrorMessage(err?.response?.data?.codigo) : "";
        setSubmitError(fieldMessage || message || "No pudimos confirmar el turno. Intentá de nuevo.");
        return "error";
      } finally {
        setSubmitting(false);
      }
    },
    [hold, token, salonId, navigate, resetAfterConflict, linkedProfessionalId, professionalId, clientFields, fieldAnswers],
  );

  // Deposit: pick the card first, then create and redirect (same order as
  // Canchas). Free: create directly.
  const onSubmit = useCallback(
    async (values) => {
      if (!hold) return null;
      const missing = clientFieldErrors(clientFields, fieldAnswers);
      if (Object.keys(missing).length) {
        setFieldErrors(missing);
        return "fields";
      }
      if (Number(hold.depositAmount) > 0) {
        setPendingValues(values);
        setModalCard(cardOptions?.[0]?.id || "");
        setShowPaymentModal(true);
        return "payment";
      }
      return proceedWithReservation(values);
    },
    [hold, cardOptions, proceedWithReservation, clientFields, fieldAnswers],
  );

  const handleConfirmPaymentSelection = useCallback(() => {
    if (!selectedCardOption || !selectedCardOption.storeId) return null;
    setShowPaymentModal(false);
    if (!pendingValues) return null;
    return proceedWithReservation(pendingValues, selectedCardOption.storeId);
  }, [selectedCardOption, pendingValues, proceedWithReservation]);

  const linked = useMemo(
    () => (linkedProfessionalId ? { professional: selectedProfessional, leave: () => onLeaveLink?.() } : null),
    [linkedProfessionalId, selectedProfessional, onLeaveLink],
  );

  return {
    salon,
    items,
    waitlistToken,
    // selection
    linked,
    cartIds,
    cartServices,
    cartChoices,
    cartLines,
    selectionComplete,
    addService,
    updateService,
    removeService,
    professionalId,
    selectedProfessional,
    professionalOptions,
    maxDurationNotice,
    selectProfessional,
    date,
    setDate,
    dateBookable,

    // totals
    baseTotals,
    effectiveTotals,
    holdTotals,

    // availability + slot
    availability,
    reloadAvailability,
    selectedSlot,
    selectSlot,
    slotEndFor,

    // hold
    hold,
    holdLoading,
    holdError,
    clearHoldError,
    holdExpired,
    createHold,
    handleHoldExpired,
    dismissExpired,
    restoredStep,

    // contact form + checkout
    register,
    handleSubmit,
    watch,
    errors,
    countryCode,
    setPhone,
    clientFields,
    fieldAnswers,
    fieldErrors,
    setFieldAnswer,
    consent,
    setConsent,
    marketingOptIn,
    setMarketingOptIn,
    submitting,
    submitError,
    onSubmit,
    showPaymentModal,
    closePaymentModal,
    modalCard,
    setModalCard,
    cardOptions,
    selectedCardOption,
    handleConfirmPaymentSelection,
  };
}
